// Scelta del cliente come nelle Dimissioni: si carica il documento d'identità, il CRM lo legge
// (OCR locale), abbina il cliente esistente per codice fiscale e gli allega il documento, oppure
// apre "Nuovo cliente" già compilato (CF calcolato, CAP, indirizzo) che viene creato con conferma().
// Uso: <ClienteConDocumento ref={r} clients={...} setClients={...} /> e poi `await r.current.conferma()`
// restituisce l'id del cliente (o null se mancano dati, con il messaggio già mostrato).
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import axios from 'axios';
import { APIProvider } from '@vis.gl/react-google-maps';
import { toast } from 'sonner';
import { Loader2, Sparkles, Upload, UserPlus, Calculator } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import ClientCompletionBanner from './ClientCompletionBanner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { DateInput } from '../ui/date-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { emptyClient, NAZIONI } from '../../constants/clientConstants';
import { calcolaCodiceFiscale } from '../../utils/fiscalCode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const OCR_DOC_TYPE_MAP = {
  'Permesso di soggiorno': 'permesso_soggiorno',
  "Carta d'identità elettronica": 'carta_identita',
  'Passaporto': 'passaporto',
  'Patente': 'patente',
};
const DOC_LABEL_MAP = {
  'Permesso di soggiorno': 'PSE',
  "Carta d'identità elettronica": 'CIE',
  'Passaporto': 'PASS',
  'Patente': 'PAT',
};

const ClienteConDocumento = forwardRef(function ClienteConDocumento(
  { clients, setClients, etichetta = 'Cliente', tipoDocumentoIniziale = 'Permesso di soggiorno', onCliente }, ref) {
  const [clientId, setClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [docTipo, setDocTipo] = useState(tipoDocumentoIniziale);
  const [extracting, setExtracting] = useState(false);
  const [newClientMode, setNewClientMode] = useState(false);
  const [newClientData, setNewClientData] = useState(emptyClient);
  const [pendingDoc, setPendingDoc] = useState(null);
  const [incompleteClient, setIncompleteClient] = useState(null);
  // chi usa il componente può sapere quale cliente è stato scelto (es. per mostrarne la scheda)
  useEffect(() => { if (onCliente) onCliente(newClientMode ? '' : clientId); }, [clientId, newClientMode, onCliente]);

  const filtered = clients.filter((c) => {
    const q = clientSearch.toLowerCase().trim();
    if (!q) return true;
    return `${c.last_name || ''} ${c.first_name || ''} ${c.first_name || ''} ${c.last_name || ''} ${c.fiscal_code || ''}`
      .toLowerCase().includes(q);
  });
  const matchClientByCf = (cf) => (cf ? clients.find((c) => (c.fiscal_code || '').toUpperCase() === cf.toUpperCase()) || null : null);

  // Allega il file ai "Documenti anagrafici" del cliente come "SIGLA COGNOME NOME"; se c'è già un
  // documento con la stessa sigla lo sostituisce (come nelle Dimissioni).
  const uploadClientDocument = async (id, file, sigla, lastName, firstName, expiryDate) => {
    try {
      const label = `${sigla} ${lastName || ''} ${firstName || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
      try {
        const res = await axios.get(`${API}/clients/${id}`);
        const existing = (res.data.documents || []).find(
          (d) => d.category === 'anagrafico' && (d.description || '').toUpperCase().startsWith(`${sigla} `));
        if (existing) await axios.delete(`${API}/clients/${id}/documents/${existing.id}`);
      } catch (_) { /* il controllo duplicati non deve bloccare */ }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', label);
      fd.append('category', 'anagrafico');
      if (expiryDate) fd.append('expiry_date', expiryDate);
      await axios.post(`${API}/clients/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (error) {
      toast.error(`Dati letti, ma il documento non è stato allegato in anagrafica: ${error.response?.data?.detail || 'errore'}`);
    }
  };

  const scegli = (c) => {
    setClientId(c.id);
    setClientSearch(`${c.last_name} ${c.first_name}`.toUpperCase());
    setNewClientMode(false);
    setIncompleteClient(c);
  };

  const leggiDocumento = async (file) => {
    if (!file) return;
    const ocrType = OCR_DOC_TYPE_MAP[docTipo];
    const sigla = DOC_LABEL_MAP[docTipo];
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', ocrType);
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const c = res.data.client_data || {};
      const expiryDate = d.expiry_date || '';
      if (!c.fiscal_code && !c.first_name && !c.last_name) {
        toast.error('Nessun dato riconosciuto: controlla di aver scelto il tipo di documento giusto');
        return;
      }
      const match = matchClientByCf(c.fiscal_code);
      if (match) {
        scegli(match);
        await uploadClientDocument(match.id, file, sigla, match.last_name, match.first_name, expiryDate);
        toast.success(`Cliente esistente abbinato: ${match.last_name} ${match.first_name} — documento allegato in anagrafica`);
        return;
      }
      let merged = {
        ...newClientData,
        first_name: (c.first_name || newClientData.first_name || '').toUpperCase(),
        last_name: (c.last_name || newClientData.last_name || '').toUpperCase(),
        fiscal_code: c.fiscal_code || newClientData.fiscal_code,
        gender: c.gender || newClientData.gender,
        birth_date: c.birth_date || newClientData.birth_date,
        birth_abroad: c.birth_abroad !== undefined ? c.birth_abroad : newClientData.birth_abroad,
        birth_comune: c.birth_comune || (c.birth_abroad ? '' : newClientData.birth_comune),
        birth_provincia: c.birth_provincia || newClientData.birth_provincia,
        birth_country: c.birth_country || newClientData.birth_country,
        address: c.address || newClientData.address,
        address_number: c.address_number || newClientData.address_number,
        city: c.city || newClientData.city,
        city_provincia: c.city_provincia || newClientData.city_provincia,
      };
      if (!merged.postal_code && merged.city && merged.city_provincia) {
        try {
          const capRes = await axios.get(`${API}/cap`, { params: { city: merged.city, provincia: merged.city_provincia } });
          if (capRes.data.single && capRes.data.auto_cap) merged = { ...merged, postal_code: capRes.data.auto_cap };
        } catch (_) { /* aiuto in più */ }
      }
      const canCalcCf = !merged.fiscal_code && merged.last_name && merged.first_name && merged.gender && merged.birth_date &&
        ((!merged.birth_abroad && merged.birth_comune && merged.birth_provincia) || (merged.birth_abroad && merged.birth_country));
      if (canCalcCf) {
        try {
          const cfRes = await axios.post(`${API}/calculate-fiscal-code`, {
            surname: merged.last_name, name: merged.first_name, birth_date: merged.birth_date, gender: merged.gender,
            birth_abroad: merged.birth_abroad, birth_comune: merged.birth_comune, birth_provincia: merged.birth_provincia,
            birth_country: merged.birth_country,
          });
          if (cfRes.data.fiscal_code) merged = { ...merged, fiscal_code: cfRes.data.fiscal_code };
        } catch (_) { /* aiuto in più */ }
      }
      const matchDopo = canCalcCf ? matchClientByCf(merged.fiscal_code) : null;
      if (matchDopo) {
        scegli(matchDopo);
        await uploadClientDocument(matchDopo.id, file, sigla, matchDopo.last_name, matchDopo.first_name, expiryDate);
        toast.success(`Cliente esistente abbinato: ${matchDopo.last_name} ${matchDopo.first_name} — documento allegato in anagrafica`);
        return;
      }
      setClientId('');
      setNewClientMode(true);
      setPendingDoc({ file, sigla, expiryDate });
      setNewClientData(merged);
      toast.success('Nuovo cliente: dati precompilati, controlla e crea la pratica (il documento si allega da solo)');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella lettura del documento');
    } finally {
      setExtracting(false);
    }
  };

  const calcolaCfDaDati = async () => {
    try {
      const cf = await calcolaCodiceFiscale(newClientData);
      setNewClientData((prev) => ({ ...prev, fiscal_code: cf }));
      const existing = matchClientByCf(cf);
      if (existing) toast.warning(`Attenzione: CF già registrato in anagrafica (${existing.last_name} ${existing.first_name})`);
      else toast.success('Codice fiscale calcolato');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const datiDaCf = async () => {
    const cf = (newClientData.fiscal_code || '').trim().toUpperCase();
    if (cf.length < 16) { toast.error('Codice fiscale non valido (servono 16 caratteri)'); return; }
    try {
      const res = await axios.post(`${API}/decode-fiscal-code`, { fiscal_code: cf });
      const bd = res.data.birth_date || '';
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(bd) ? bd : (() => { const p = bd.split('/'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : ''; })();
      setNewClientData((prev) => ({
        ...prev, gender: res.data.gender || prev.gender, birth_date: iso || prev.birth_date,
        ...(res.data.birth_abroad !== undefined ? {
          birth_abroad: res.data.birth_abroad, birth_country: res.data.birth_country || '',
          birth_comune: res.data.birth_comune || '', birth_provincia: res.data.birth_provincia || '',
        } : {}),
      }));
      toast.success('Sesso, data e luogo di nascita calcolati dal codice fiscale');
    } catch (_) {
      toast.error('Impossibile calcolare i dati dal codice fiscale');
    }
  };

  useImperativeHandle(ref, () => ({
    async conferma() {
      if (!newClientMode) {
        if (!clientId) toast.error('Scegli il cliente o carica il suo documento');
        return clientId || null;
      }
      const n = newClientData;
      if (!n.last_name || !n.first_name) { toast.error('Compila almeno cognome e nome del nuovo cliente'); return null; }
      if (!n.birth_abroad && (!n.birth_comune || !n.birth_provincia)) {
        toast.error('Compila comune e provincia di nascita (o attiva "Nato/a all\'estero")'); return null;
      }
      if (n.birth_abroad && !n.birth_country) { toast.error('Seleziona la nazione di nascita'); return null; }
      try {
        const res = await axios.post(`${API}/clients`, n);
        const id = res.data.id;
        if (pendingDoc) {
          await uploadClientDocument(id, pendingDoc.file, pendingDoc.sigla, n.last_name, n.first_name, pendingDoc.expiryDate);
          setPendingDoc(null);
        }
        setClients((prev) => [...prev, res.data]);
        toast.success('Nuovo cliente registrato in anagrafica');
        return id;
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nella registrazione del nuovo cliente');
        return null;
      }
    },
  }));

  const set = (k, v) => setNewClientData((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-3">
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 text-violet-800 font-medium text-sm">
          <Sparkles className="w-4 h-4" /> Cliente dal documento d'identità
        </div>
        <p className="text-xs text-violet-700">
          Carica il documento: se il cliente c'è già lo trovo dal codice fiscale, altrimenti ti preparo il nuovo cliente.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={docTipo} onValueChange={setDocTipo}>
            <SelectTrigger className="bg-white w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.keys(OCR_DOC_TYPE_MAP).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className={`flex-1 flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extracting ? 'opacity-60 pointer-events-none' : ''}`}>
            {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Carica {docTipo}
            <input type="file" accept="application/pdf,image/*" className="hidden"
              onChange={(e) => { leggiDocumento(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        </div>
      </div>

      <ClientCompletionBanner
        client={incompleteClient}
        onSaved={(updated) => {
          setIncompleteClient(updated);
          setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
        }}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>{etichetta}</Label>
          <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs gap-1"
            onClick={() => setNewClientMode((m) => !m)}>
            <UserPlus className="w-3.5 h-3.5" />
            {newClientMode ? 'Seleziona cliente esistente' : 'Nuovo cliente'}
          </Button>
        </div>
        {!newClientMode ? (
          <div className="relative">
            <Input placeholder="Cerca cliente per nome o codice fiscale..." value={clientSearch}
              onChange={(e) => { setClientSearch(e.target.value); setShowDropdown(true); setClientId(''); }}
              onFocus={() => setShowDropdown(true)} onBlur={() => setTimeout(() => setShowDropdown(false), 200)} />
            {showDropdown && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                {filtered.length === 0 ? (
                  <div className="p-3 text-center text-slate-500 text-sm">Nessun cliente trovato</div>
                ) : filtered.slice(0, 10).map((c) => (
                  <div key={c.id} onMouseDown={(e) => { e.preventDefault(); scegli(c); setShowDropdown(false); }}
                    className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${clientId === c.id ? 'bg-blue-50' : ''}`}>
                    <div className="font-medium uppercase">{c.last_name} {c.first_name}</div>
                    <div className="text-sm text-slate-500 font-mono">{c.fiscal_code}</div>
                  </div>
                ))}
              </div>
            )}
            {clientId && <p className="text-sm text-green-600 mt-2">Cliente selezionato</p>}
          </div>
        ) : (
          <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
            <p className="text-xs text-slate-500">Il cliente non è ancora in anagrafica: viene creato insieme alla pratica.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Cognome</Label>
                <Input value={newClientData.last_name} onChange={(e) => set('last_name', e.target.value.toUpperCase())} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Nome</Label>
                <Input value={newClientData.first_name} onChange={(e) => set('first_name', e.target.value.toUpperCase())} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Sesso</Label>
                <Select value={newClientData.gender} onValueChange={(v) => set('gender', v)}>
                  <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschio</SelectItem>
                    <SelectItem value="F">Femmina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Data di nascita</Label>
                <DateInput value={newClientData.birth_date} onChange={(val) => set('birth_date', val)} placeholder="gg/mm/aaaa" />
              </div>
            </div>
            <div className="p-3 bg-white border rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-500">Nato/a all'estero</Label>
                <Switch checked={newClientData.birth_abroad}
                  onCheckedChange={(checked) => setNewClientData((prev) => ({
                    ...prev, birth_abroad: checked, birth_comune: '', birth_provincia: '', birth_country: checked ? 'Cina' : '',
                  }))} />
              </div>
              {!newClientData.birth_abroad ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Comune di nascita</Label>
                    <Input value={newClientData.birth_comune} onChange={(e) => set('birth_comune', e.target.value.toUpperCase())} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Provincia (sigla)</Label>
                    <Input value={newClientData.birth_provincia} maxLength={2} className="uppercase"
                      onChange={(e) => set('birth_provincia', e.target.value.toUpperCase())} />
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Nazione di nascita</Label>
                  <Select value={newClientData.birth_country} onValueChange={(v) => set('birth_country', v)}>
                    <SelectTrigger><SelectValue placeholder="Seleziona nazione" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {NAZIONI.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Codice fiscale</Label>
              <div className="flex gap-2">
                <Input value={newClientData.fiscal_code} className="uppercase"
                  onChange={(e) => set('fiscal_code', e.target.value.toUpperCase())} />
                <Button type="button" variant="outline" size="sm" onClick={calcolaCfDaDati}>Calcola CF</Button>
                <Button type="button" variant="outline" size="sm" onClick={datiDaCf} title="Calcola sesso e data di nascita dal CF">
                  <Calculator className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Indirizzo di residenza</Label>
                {GOOGLE_MAPS_API_KEY ? (
                  <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                    <AddressAutocomplete value={newClientData.address} defaultValue={newClientData.address}
                      placeholder="es. Via Roma..."
                      onPlaceSelect={(d) => setNewClientData((prev) => ({
                        ...prev,
                        address: (d.address || prev.address || '').toUpperCase(),
                        address_number: (d.address_number || prev.address_number || '').toUpperCase(),
                        city: (d.city || prev.city || '').toUpperCase(),
                        city_provincia: (d.city_provincia || prev.city_provincia || '').toUpperCase(),
                        postal_code: d.postal_code || prev.postal_code,
                      }))} />
                  </APIProvider>
                ) : (
                  <Input value={newClientData.address} onChange={(e) => set('address', e.target.value.toUpperCase())} />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Numero civico</Label>
                <Input value={newClientData.address_number} onChange={(e) => set('address_number', e.target.value.toUpperCase())} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs text-slate-500">Città di residenza</Label>
                <Input value={newClientData.city} onChange={(e) => set('city', e.target.value.toUpperCase())} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Provincia</Label>
                <Input value={newClientData.city_provincia} maxLength={2} className="uppercase"
                  onChange={(e) => set('city_provincia', e.target.value.toUpperCase())} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">CAP</Label>
                <Input value={newClientData.postal_code} onChange={(e) => set('postal_code', e.target.value)} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs text-slate-500">Cellulare</Label>
                <Input value={newClientData.phone} onChange={(e) => set('phone', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Email</Label>
              <Input type="email" value={newClientData.email} onChange={(e) => set('email', e.target.value.toUpperCase())} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ClienteConDocumento;
